import React, { useMemo } from 'react';

const buildMeshPaths = (count, seedOffset = 0, variant = 'upper') => {
  const paths = [];

  for (let i = 0; i < count; i += 1) {
    const t = i / (count - 1);
    const offset = t * 170;
    const waveLift = Math.sin((i + seedOffset) * 0.32) * 5;

    if (variant === 'lower') {
      paths.push(
        [
          `M -70 ${(452 + offset + waveLift).toFixed(1)}`,
          `C 0 ${(404 + offset * 0.82).toFixed(1)}, 48 ${(424 + offset * 0.64).toFixed(1)}, 104 ${(488 + offset * 0.46).toFixed(1)}`,
          `C 158 ${(550 + offset * 0.35).toFixed(1)}, 208 ${(552 + offset * 0.48).toFixed(1)}, 262 ${(498 + offset * 0.62).toFixed(1)}`,
          `C 314 ${(444 + offset * 0.78).toFixed(1)}, 356 ${(452 + offset * 0.88).toFixed(1)}, 410 ${(506 + offset * 0.8).toFixed(1)}`
        ].join(' ')
      );
      continue;
    }

    paths.push(
      [
        `M -70 ${(382 + offset + waveLift).toFixed(1)}`,
        `C -4 ${(334 + offset * 0.82).toFixed(1)}, 46 ${(342 + offset * 0.64).toFixed(1)}, 104 ${(414 + offset * 0.45).toFixed(1)}`,
        `C 158 ${(490 + offset * 0.34).toFixed(1)}, 208 ${(494 + offset * 0.5).toFixed(1)}, 260 ${(430 + offset * 0.64).toFixed(1)}`,
        `C 306 ${(376 + offset * 0.8).toFixed(1)}, 354 ${(382 + offset * 0.9).toFixed(1)}, 410 ${(436 + offset * 0.82).toFixed(1)}`
      ].join(' ')
    );
  }

  return paths;
};

const PARTICLE_POSITIONS = [
  [12, 22], [28, 46], [45, 16], [62, 58], [78, 32],
  [18, 70], [35, 80], [52, 64], [70, 84], [88, 50],
  [8, 54], [42, 35], [58, 22], [75, 72], [92, 26],
  [22, 90], [48, 92], [65, 42], [82, 60], [15, 38],
  [38, 60], [55, 76], [72, 18], [85, 86], [30, 28],
  [60, 50], [25, 64], [50, 30], [68, 92], [90, 66],
];

const SidebarWave = () => {
  const meshA = useMemo(() => buildMeshPaths(72, 0, 'upper'), []);
  const meshB = useMemo(() => buildMeshPaths(64, 9, 'lower'), []);

  return (
    <div className="sidebar-wave" aria-hidden="true">
      <div className="sidebar-wave-glow" />

      <svg
        className="sidebar-wave-mesh sidebar-wave-mesh-a"
        viewBox="0 0 320 620"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="sidebarWavePurpleA" x1="0" y1="300" x2="320" y2="470" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#4C1D95" stopOpacity="0.08" />
            <stop offset="28%" stopColor="#6D28D9" stopOpacity="0.54" />
            <stop offset="48%" stopColor="#D8B4FE" stopOpacity="0.95" />
            <stop offset="68%" stopColor="#8B5CF6" stopOpacity="0.56" />
            <stop offset="100%" stopColor="#4F46E5" stopOpacity="0.12" />
          </linearGradient>
          <linearGradient id="sidebarWaveHotA" x1="0" y1="355" x2="320" y2="430" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#7C3AED" stopOpacity="0.2" />
            <stop offset="42%" stopColor="#F0ABFC" stopOpacity="0.98" />
            <stop offset="100%" stopColor="#7C3AED" stopOpacity="0.22" />
          </linearGradient>
          <filter id="sidebarWaveBloomA" x="-20%" y="-30%" width="140%" height="160%">
            <feGaussianBlur stdDeviation="1.15" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {meshA.map((d, i) => (
          <path
            key={`a-${i}`}
            d={d}
            fill="none"
            stroke="url(#sidebarWavePurpleA)"
            strokeWidth={i % 18 === 0 ? '1.05' : '0.34'}
            strokeOpacity={i % 18 === 0 ? '0.88' : '0.46'}
            strokeLinecap="round"
            filter={i % 18 === 0 ? 'url(#sidebarWaveBloomA)' : undefined}
          />
        ))}
        <path
          d="M -70 430 C 0 370, 50 368, 112 438 C 164 496, 210 506, 262 450 C 306 404, 352 406, 410 462"
          fill="none"
          stroke="url(#sidebarWaveHotA)"
          strokeWidth="1.75"
          strokeLinecap="round"
          filter="url(#sidebarWaveBloomA)"
        />
        <path
          d="M -70 462 C 0 402, 50 408, 112 470 C 164 532, 210 536, 262 482 C 306 438, 352 440, 410 498"
          fill="none"
          stroke="url(#sidebarWaveHotA)"
          strokeWidth="1.25"
          strokeOpacity="0.88"
          strokeLinecap="round"
          filter="url(#sidebarWaveBloomA)"
        />
      </svg>

      <svg
        className="sidebar-wave-mesh sidebar-wave-mesh-b"
        viewBox="0 0 320 620"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="sidebarWavePurpleB" x1="0" y1="360" x2="320" y2="500" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#6D28D9" stopOpacity="0.08" />
            <stop offset="40%" stopColor="#8B5CF6" stopOpacity="0.38" />
            <stop offset="57%" stopColor="#E879F9" stopOpacity="0.72" />
            <stop offset="100%" stopColor="#6366F1" stopOpacity="0.14" />
          </linearGradient>
          <linearGradient id="sidebarWaveHotB" x1="0" y1="440" x2="320" y2="520" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#6D28D9" stopOpacity="0.12" />
            <stop offset="52%" stopColor="#F0ABFC" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#6366F1" stopOpacity="0.16" />
          </linearGradient>
          <filter id="sidebarWaveBloomB" x="-20%" y="-30%" width="140%" height="160%">
            <feGaussianBlur stdDeviation="1.35" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {meshB.map((d, i) => (
          <path
            key={`b-${i}`}
            d={d}
            fill="none"
            stroke="url(#sidebarWavePurpleB)"
            strokeWidth={i % 20 === 0 ? '0.96' : '0.3'}
            strokeOpacity={i % 20 === 0 ? '0.72' : '0.32'}
            strokeLinecap="round"
            filter={i % 20 === 0 ? 'url(#sidebarWaveBloomB)' : undefined}
          />
        ))}
        <path
          d="M -70 490 C 0 436, 52 446, 112 506 C 164 562, 210 570, 262 516 C 310 468, 352 476, 410 532"
          fill="none"
          stroke="url(#sidebarWaveHotB)"
          strokeWidth="1.35"
          strokeLinecap="round"
          filter="url(#sidebarWaveBloomB)"
        />
      </svg>

      <div className="sidebar-wave-particles">
        {PARTICLE_POSITIONS.map(([left, bottom], i) => (
          <span
            key={`p-${i}`}
            className="sidebar-wave-particle"
            style={{
              left: `${left}%`,
              bottom: `${bottom}%`,
              animationDelay: `${(i % 10) * 0.5}s`,
              animationDuration: `${4.5 + (i % 5) * 0.4}s`,
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default SidebarWave;
